import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import {
  Phone, ChatCircleDots, Star, WarningCircle, CheckCircle, ArrowLeft,
  PaperPlaneTilt, Drop, CaretRight, SpinnerGap,
  IdentificationCard, LockKey, ArrowClockwise, Wallet, ShieldCheck, ArrowRight,
  ArrowDown, ArrowUp,
} from '@phosphor-icons/react';

const NAVY = '#0A2540';

export default function PublicHubPage() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState('home');

  useEffect(() => {
    axios.get(`${API_URL}/api/public/hub/${slug}`)
      .then((r) => setHub(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <Screen><div className="flex justify-center py-20"><SpinnerGap size={30} className="animate-spin text-[#0A2540]" /></div></Screen>;
  }
  if (notFound || !hub) {
    return (
      <Screen>
        <div className="text-center py-16" data-testid="hub-not-found">
          <WarningCircle size={44} weight="fill" className="text-[#B4453A] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#0A2540]">Link not found</h1>
          <p className="text-sm text-stone-500 mt-1">This QR code is no longer valid. Please ask staff for an updated one.</p>
        </div>
      </Screen>
    );
  }

  const tiles = hub.tiles || {};

  if (view === 'home') {
    return (
      <div className="min-h-screen bg-[#0A2540] flex flex-col" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        <div className="max-w-md w-full mx-auto px-6 py-12 flex-1 flex flex-col justify-center" data-testid="hub-home">
          <div className="text-center mb-9">
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="hub-welcome-heading">Welcome to Ecowater</h1>
            <p className="text-base text-white/70 mt-3">How can we help you today?</p>
          </div>
          <div className="space-y-3">
            {tiles.concern !== false && (
              <Tile icon={ChatCircleDots} title="Report a concern" desc="Tell us about a problem — we'll follow up" onClick={() => setView('concern')} testid="tile-concern" />
            )}
            {tiles.feedback !== false && (
              <Tile icon={Star} title="Give feedback" desc="Rate your experience & leave a comment" onClick={() => setView('feedback')} testid="tile-feedback" />
            )}
            {tiles.my_account && (
              <Tile icon={IdentificationCard} title="My account" desc="Check your balance & recent activity" onClick={() => setView('account')} testid="tile-account" />
            )}
            {tiles.call_us !== false && (
              <Tile icon={Phone} title="Call us" desc="Reach our team by phone" onClick={() => setView('call')} testid="tile-call" />
            )}
            {tiles.register && (
              <Tile icon={Drop} title="Register" desc="Coming soon" disabled testid="tile-register" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Screen>
      {view === 'call' && <CallUs hub={hub} onBack={() => setView('home')} />}
      {view === 'concern' && <ConcernForm slug={slug} hub={hub} onBack={() => setView('home')} />}
      {view === 'feedback' && <FeedbackForm slug={slug} hub={hub} onBack={() => setView('home')} />}
      {view === 'account' && <AccountFlow slug={slug} onBack={() => setView('home')} />}
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen bg-[#FCFAF6]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <div className="max-w-md mx-auto px-5 py-8">{children}</div>
    </div>
  );
}

function Tile({ icon: Icon, title, desc, onClick, disabled, testid }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-200 ${
        disabled
          ? 'border-white/10 bg-white/5 opacity-50 cursor-default'
          : 'border-white/25 bg-white/5 hover:bg-white/10 hover:border-white/50 active:scale-[0.99]'
      }`}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-white/10">
        <Icon size={22} weight="duotone" className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white">{title}</div>
        <div className="text-xs text-white/60 truncate">{desc}</div>
      </div>
      {!disabled && <CaretRight size={18} className="text-white/40 shrink-0" />}
    </button>
  );
}

function BackBar({ title, onBack }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      <button onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid="hub-back">
        <ArrowLeft size={17} className="text-[#0A2540]" />
      </button>
      <h1 className="text-lg font-bold text-[#0A2540]">{title}</h1>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-2xl border border-[#EDE6D8] bg-white p-5">{children}</div>;
}

function answersToArray(answers) {
  return Object.entries(answers || {})
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([id, value]) => ({ id, value }));
}

function firstMissingRequired(questions, answers) {
  for (const q of (questions || [])) {
    if (q.required) {
      const v = answers[q.id];
      if (v === '' || v === null || v === undefined) return q.label;
    }
  }
  return null;
}

function DynamicQuestions({ questions, answers, setAnswers, idPrefix }) {
  if (!questions || questions.length === 0) return null;
  const setVal = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  return (
    <>
      {questions.map((q) => (
        <Field key={q.id} label={q.label + (q.required ? ' *' : '')}>
          {q.type === 'long_text' && (
            <textarea value={answers[q.id] || ''} onChange={(e) => setVal(q.id, e.target.value)} rows={3}
              className={inputCls + ' h-auto py-2.5 resize-none'} data-testid={`${idPrefix}-q-${q.id}`} />
          )}
          {q.type === 'short_text' && (
            <input value={answers[q.id] || ''} onChange={(e) => setVal(q.id, e.target.value)}
              className={inputCls} data-testid={`${idPrefix}-q-${q.id}`} />
          )}
          {q.type === 'dropdown' && (
            <select value={answers[q.id] || ''} onChange={(e) => setVal(q.id, e.target.value)} className={inputCls} data-testid={`${idPrefix}-q-${q.id}`}>
              <option value="">Select…</option>
              {(q.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
            </select>
          )}
          {q.type === 'rating' && (
            <div className="flex gap-2 mt-1" data-testid={`${idPrefix}-q-${q.id}`}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} type="button" onClick={() => setVal(q.id, s)} className="flex-1 flex justify-center py-1">
                  <Star size={28} weight={(answers[q.id] || 0) >= s ? 'fill' : 'regular'} className={(answers[q.id] || 0) >= s ? 'text-[#E0A82E]' : 'text-stone-300'} />
                </button>
              ))}
            </div>
          )}
          {q.type === 'yesno' && (
            <div className="flex gap-2" data-testid={`${idPrefix}-q-${q.id}`}>
              {['Yes', 'No'].map((v) => (
                <button key={v} type="button" onClick={() => setVal(q.id, v)}
                  className={`flex-1 h-11 rounded-lg text-sm font-semibold border transition-colors ${answers[q.id] === v ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-stone-600 border-[#EDE6D8] hover:border-[#0A2540]/40'}`}>
                  {v}
                </button>
              ))}
            </div>
          )}
        </Field>
      ))}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full h-11 px-3 rounded-lg border border-[#EDE6D8] bg-white text-sm text-[#1C1917] placeholder:text-stone-300 focus:outline-none focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540]/20';

function SubmitBtn({ loading, children, testid }) {
  return (
    <button type="submit" disabled={loading} data-testid={testid}
      className="w-full h-12 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:shadow-lg hover:shadow-[#0A2540]/20"
      style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
      {loading ? <SpinnerGap size={18} className="animate-spin" /> : <PaperPlaneTilt size={17} />}
      {children}
    </button>
  );
}

function Done({ icon: Icon, title, sub, extra, onBack }) {
  return (
    <div className="text-center py-10" data-testid="hub-success">
      <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#DCEBE2] flex items-center justify-center">
        <Icon size={34} weight="fill" className="text-[#2F6D4B]" />
      </div>
      <h2 className="text-xl font-bold text-[#0A2540]">{title}</h2>
      <p className="text-sm text-stone-500 mt-2">{sub}</p>
      {extra}
      <button onClick={onBack} className="block mx-auto mt-6 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="hub-done-back">Back to home</button>
    </div>
  );
}

function CallUs({ hub, onBack }) {
  const entityNums = hub.call_us || [];
  const campusNums = (hub.campuses || []).filter((c) => (c.call_us || []).length);
  const hasAny = entityNums.length || campusNums.length;
  return (
    <div data-testid="hub-call">
      <BackBar title="Call us" onBack={onBack} />
      {!hasAny ? (
        <Card><p className="text-sm text-stone-500 text-center py-4">No phone numbers have been published yet. Please check back later.</p></Card>
      ) : (
        <div className="space-y-3">
          {entityNums.map((n, i) => (
            <a key={`e${i}`} href={`tel:+${n.number}`} className="flex items-center gap-3 p-4 rounded-2xl border border-[#EDE6D8] bg-white hover:border-[#0A2540]/30" data-testid={`call-number-${i}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(10,37,64,0.06)' }}><Phone size={20} weight="fill" className="text-[#0A2540]" /></div>
              <div className="flex-1"><div className="font-semibold text-[#1C1917]">{n.label || 'Call us'}</div><div className="text-sm text-[#0991d6]">+{n.number}</div></div>
              <CaretRight size={18} className="text-stone-300" />
            </a>
          ))}
          {campusNums.map((c) => (
            <div key={c.id}>
              <div className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mt-2 mb-1 px-1">{c.name}</div>
              {(c.call_us || []).map((num, i) => (
                <a key={`${c.id}-${i}`} href={`tel:+${num}`} className="flex items-center gap-3 p-4 rounded-2xl border border-[#EDE6D8] bg-white hover:border-[#0A2540]/30 mb-2">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(10,37,64,0.06)' }}><Phone size={20} weight="fill" className="text-[#0A2540]" /></div>
                  <div className="flex-1"><div className="text-sm text-[#0991d6]">+{num}</div></div>
                  <CaretRight size={18} className="text-stone-300" />
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConcernForm({ slug, hub, onBack }) {
  const cfg = (hub.forms && hub.forms.concern) || {};
  const cats = cfg.categories || hub.categories || ['other'];
  const questions = cfg.questions || [];
  const [category, setCategory] = useState(cats[0] || 'other');
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [campusId, setCampusId] = useState('');
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ref, setRef] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (message.trim().length < 3) { setError('Please describe your concern'); return; }
    const missing = firstMissingRequired(questions, answers);
    if (missing) { setError(`Please answer: ${missing}`); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/hub/${slug}/concern`, {
        category, message, name, phone, email, campus_id: campusId || null,
        answers: answersToArray(answers),
      });
      setRef(r.data.reference);
    } catch (err) {
      setError(formatApiError(err, 'Could not submit. Please try again.'));
    } finally { setLoading(false); }
  };

  if (ref) {
    return <Done icon={CheckCircle} title="Concern received" sub={cfg.thank_you || 'Our customer experience team will follow up.'} onBack={onBack}
      extra={<div className="mt-3 inline-block px-4 py-2 rounded-lg bg-[#FCFAF6] border border-[#EDE6D8] text-sm"><span className="text-stone-500">Reference </span><b className="text-[#0A2540]" data-testid="concern-ref">{ref}</b></div>} />;
  }

  return (
    <div data-testid="hub-concern">
      <BackBar title={cfg.title || 'Report a concern'} onBack={onBack} />
      {cfg.intro && <p className="text-sm text-stone-500 -mt-2 mb-4">{cfg.intro}</p>}
      <Card>
        {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="concern-error"><WarningCircle size={15} />{error}</div>}
        <form onSubmit={submit} className="space-y-4">
          <Field label="What's it about?">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="concern-category">
              {cats.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </Field>
          {(hub.campuses || []).length > 0 && (
            <Field label="Location (optional)">
              <select value={campusId} onChange={(e) => setCampusId(e.target.value)} className={inputCls} data-testid="concern-campus">
                <option value="">Select a location…</option>
                {hub.campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Describe your concern">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Tell us what happened…" className={inputCls + ' h-auto py-2.5 resize-none'} data-testid="concern-message" />
          </Field>
          <DynamicQuestions questions={questions} answers={answers} setAnswers={setAnswers} idPrefix="concern" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your name"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" className={inputCls} data-testid="concern-name" /></Field>
            <Field label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" className={inputCls} data-testid="concern-phone" /></Field>
          </div>
          <Field label="Email (for updates)"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Optional" className={inputCls} data-testid="concern-email" /></Field>
          <SubmitBtn loading={loading} testid="concern-submit">Submit concern</SubmitBtn>
        </form>
      </Card>
    </div>
  );
}

function FeedbackForm({ slug, hub, onBack }) {
  const cfg = (hub.forms && hub.forms.feedback) || {};
  const questions = cfg.questions || [];
  const showCsat = cfg.csat_enabled !== false;
  const showNps = cfg.nps_enabled !== false;
  const showComment = cfg.comment_enabled !== false;
  const [csat, setCsat] = useState(0);
  const [nps, setNps] = useState(null);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const hasCustom = answersToArray(answers).length > 0;
    if (!csat && nps === null && !comment.trim() && !hasCustom) { setError('Please leave a rating or a comment'); return; }
    const missing = firstMissingRequired(questions, answers);
    if (missing) { setError(`Please answer: ${missing}`); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/public/hub/${slug}/feedback`, {
        csat: (showCsat && csat) ? csat : null,
        nps: showNps ? nps : null,
        comment: showComment ? comment : '',
        name,
        form_id: cfg.id || null,
        answers: answersToArray(answers),
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, 'Could not submit. Please try again.'));
    } finally { setLoading(false); }
  };

  if (done) return <Done icon={Star} title="Thank you!" sub={cfg.thank_you || 'Your feedback helps us serve you better.'} onBack={onBack} />;

  return (
    <div data-testid="hub-feedback">
      <BackBar title={cfg.title || 'Give feedback'} onBack={onBack} />
      {cfg.intro && <p className="text-sm text-stone-500 -mt-2 mb-4">{cfg.intro}</p>}
      <Card>
        {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="feedback-error"><WarningCircle size={15} />{error}</div>}
        <form onSubmit={submit} className="space-y-5">
          {showCsat && (
            <div>
              <label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">How was your experience?</label>
              <div className="flex justify-between gap-2 mt-2" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" onClick={() => setCsat(s)} data-testid={`star-${s}`} className="flex-1 flex justify-center py-1">
                    <Star size={34} weight={csat >= s ? 'fill' : 'regular'} className={csat >= s ? 'text-[#E0A82E]' : 'text-stone-300'} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {showNps && (
            <div>
              <label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">How likely are you to recommend us? (0–10)</label>
              <div className="grid grid-cols-11 gap-1 mt-2" data-testid="feedback-nps">
                {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                  <button key={n} type="button" onClick={() => setNps(n)} data-testid={`nps-${n}`}
                    className={`h-9 rounded-md text-xs font-semibold border transition-colors ${nps === n ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-stone-600 border-[#EDE6D8] hover:border-[#0A2540]/40'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showComment && (
            <Field label="Comments (optional)">
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="What did you love, or what can we improve?" className={inputCls + ' h-auto py-2.5 resize-none'} data-testid="feedback-comment" />
            </Field>
          )}
          <DynamicQuestions questions={questions} answers={answers} setAnswers={setAnswers} idPrefix="feedback" />
          <Field label="Your name (optional)"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" className={inputCls} data-testid="feedback-name" /></Field>
          <SubmitBtn loading={loading} testid="feedback-submit">Send feedback</SubmitBtn>
        </form>
      </Card>
    </div>
  );
}


function AccountFlow({ slug, onBack }) {
  const [step, setStep] = useState('card');   // card | otp | snapshot
  const [card, setCard] = useState('');
  const [otp, setOtp] = useState('');
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    setError(''); setNotice('');
    if (card.replace(/\D/g, '').length < 4) { setError('Enter your card number'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/hub/${slug}/account/otp/send`, { card_number: card });
      if (r.data?.action === 'visit_store') { setNotice(r.data.message); return; }
      setStep('otp');
      setCooldown(r.data?.cooldown || 60);
      if (r.data?.dev_otp) { setOtp(r.data.dev_otp); setNotice(`Preview mode — code auto-filled (${r.data.dev_otp})`); }
    } catch (err) {
      setError(formatApiError(err, 'Could not send code. Please try again.'));
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.replace(/\D/g, '').length < 4) { setError('Enter the code'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/hub/${slug}/account/otp/verify`, { card_number: card, otp });
      const s = await axios.get(`${API_URL}/api/public/hub/${slug}/account`, { headers: { Authorization: `Bearer ${r.data.token}` } });
      setSnap(s.data);
      setStep('snapshot');
    } catch (err) {
      setError(formatApiError(err, 'Verification failed.'));
    } finally { setLoading(false); }
  };

  const money = (v, ccy) => `${ccy || ''} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim();

  return (
    <div data-testid="hub-account">
      <BackBar title="My account" onBack={onBack} />

      {step === 'card' && (
        <Card>
          <div className="flex items-start gap-2 bg-[#F4F8FC] border border-[#E4ECF5] rounded-xl p-3 mb-4">
            <ShieldCheck size={18} className="text-[#0A2540] mt-0.5 shrink-0" />
            <p className="text-xs text-stone-600">Enter your card number. We'll send a one-time code to the phone number registered on your account.</p>
          </div>
          {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="account-error"><WarningCircle size={15} />{error}</div>}
          {notice && <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="account-notice"><WarningCircle size={15} />{notice}</div>}
          <form onSubmit={sendOtp} className="space-y-4">
            <Field label="Card number">
              <div className="relative">
                <IdentificationCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input value={card} onChange={(e) => setCard(e.target.value)} inputMode="numeric" placeholder="1370000000000"
                  className={inputCls + ' pl-10'} data-testid="account-card" />
              </div>
            </Field>
            <SubmitBtn loading={loading} testid="account-send-otp">Send code</SubmitBtn>
          </form>
        </Card>
      )}

      {step === 'otp' && (
        <Card>
          <p className="text-sm text-stone-600 mb-4">Enter the 6-digit code we sent to the phone on file for card <b className="text-[#0A2540]">•••• {card.replace(/\D/g, '').slice(-4)}</b>.</p>
          {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="account-error"><WarningCircle size={15} />{error}</div>}
          {notice && <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg mb-4 text-xs" data-testid="account-notice">{notice}</div>}
          <form onSubmit={verify} className="space-y-4">
            <Field label="One-time code">
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" maxLength={6} placeholder="••••••"
                  className={inputCls + ' pl-10 tracking-[0.4em] font-semibold'} data-testid="account-otp" />
              </div>
            </Field>
            <SubmitBtn loading={loading} testid="account-verify"><ArrowRight size={17} />View my account</SubmitBtn>
          </form>
          <button onClick={() => sendOtp()} disabled={cooldown > 0 || loading} data-testid="account-resend"
            className="w-full mt-3 h-9 text-sm font-medium text-[#0A2540] disabled:text-stone-400 flex items-center justify-center gap-1.5">
            <ArrowClockwise size={15} />{cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
          </button>
        </Card>
      )}

      {step === 'snapshot' && snap && (
        <div className="space-y-4" data-testid="account-snapshot">
          <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/60">Wallet balance</div>
                <div className="text-3xl font-bold mt-1" data-testid="account-balance">{money(snap.balance, snap.currency)}</div>
              </div>
              <Wallet size={30} weight="duotone" className="text-white/70" />
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-white/80">{snap.name}</span>
              <span className="font-mono text-white/90">{snap.card_masked}</span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-white/60">
              {snap.tier && <span className="px-2 py-0.5 rounded-full bg-white/15">{snap.tier}</span>}
              {snap.campus && <span className="truncate">{snap.campus}</span>}
            </div>
          </div>

          {snap.low_balance && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2.5 rounded-xl text-sm" data-testid="account-lowbalance">
              <WarningCircle size={17} weight="fill" />Your balance is running low — top up at any store to keep refilling.
            </div>
          )}

          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Recent activity</div>
            {(snap.transactions || []).length === 0 ? (
              <p className="text-sm text-stone-400 py-3 text-center">No recent transactions.</p>
            ) : (
              <div className="divide-y divide-[#F1EBE0]">
                {snap.transactions.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`account-txn-${i}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.direction === 'credit' ? 'bg-[#DCEBE2]' : 'bg-[#FCEBE9]'}`}>
                      {t.direction === 'credit' ? <ArrowDown size={16} weight="bold" className="text-[#2F6D4B]" /> : <ArrowUp size={16} weight="bold" className="text-[#B4453A]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1917]">{t.type === 'RECHARGE' ? 'Top-up' : (t.type === 'REFILL' ? 'Water refill' : 'Adjustment')}</div>
                      <div className="text-[11px] text-stone-400 truncate">{t.trn_date}{t.store ? ` · ${t.store}` : ''}</div>
                    </div>
                    <div className={`text-sm font-semibold ${t.direction === 'credit' ? 'text-[#2F6D4B]' : 'text-[#B4453A]'}`}>
                      {t.direction === 'credit' ? '+' : '−'}{money(t.amount, snap.currency)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-center text-[11px] text-stone-400">Read-only view · session ends automatically for your security.</p>
          <button onClick={onBack} className="block mx-auto text-sm font-semibold text-[#0A2540] hover:underline" data-testid="account-done">Done</button>
        </div>
      )}
    </div>
  );
}
